import {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { useCreatePortfolioSelectors, useCreatePortfolioState } from 'datasource/CreatePortfolio';
import { usePortfolioProcessNavigation } from 'hooks/helpers/usePortfolioProcessNavigation';
import { isThreeBProduct } from 'hooks/isThreeBProduct';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { memberIdSelector } from 'redux-store/auth/authSelectors';
import { getClientId } from 'utils';
import { IsAllDataNull, validateSelect } from 'validation/validation';
import { getSetupData } from 'additiv-services/setup';
import { validate } from '../validation';

const setupData = getSetupData();

export const useFinalization = ({ dfsClientId }) => {
    const { i18n: { language }, t } = useTranslation();

    const { currentInvestmentApplicationId } = useCreatePortfolioState();

    const {
        selectedProductSelector,
        currentInvestmentApplicationSelector,
    } = useCreatePortfolioSelectors();

    const [pageErrors, setPageErrors] = useState(undefined);
    const isThreeB = isThreeBProduct({ product: selectedProductSelector });
    const [formData, setFormData] = useState<any>({
        isConsultationConducted: false,
        transactionByExternalReferrer: false,
    });
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSending, setSending] = useState(false);
    const clientId = getClientId(dfsClientId);
    const memberId = useSelector(memberIdSelector);
    const [error, setError] = useState(undefined);

    const containerId = useMemo(() => currentInvestmentApplicationSelector?.investmentDescription?.containerId, [currentInvestmentApplicationSelector?.investmentDescription?.containerId]);
    const isFollowUpInvestorProfileCase = useMemo(() => currentInvestmentApplicationSelector?.investmentDescription?.changeInvestmentAmount?.isFollowUpInvestorProfile, [currentInvestmentApplicationSelector?.investmentDescription?.changeInvestmentAmount?.isFollowUpInvestorProfile]);

    const {
        data: appsByContainerData,
        isFetching: loadingAppsByContainer,
        error: appsByContainerError,
    } = useGetCustomService({
        service: 'customInvestmentService',
        api: 'getInvestmentAppsByContainerId',
        payload: { containerId, language },
        enabled: !!containerId,
        gcTime: 10,
        staleTime: 10,
        refetchOnWindowFocus: false,
    });

    const appsList = useMemo(() => appsByContainerData?.data ?? [], [appsByContainerData]);

    useEffect(() => {
        if (appsByContainerError) {
            setPageErrors({
                message: appsByContainerError.message || t('general.somethingWentWrong'),
            });
        }
    }, [appsByContainerError]);

    const { mutateAsync: finalizeMutate } = useGenericAPIMutation();

    const onContinue = useCallback(async ({ withNewAPI = false }) => {
        setIsSubmitted(true);
        const errors = validate(formData) as any;

        setError(errors);

        if (IsAllDataNull(errors) === false) {
            return;
        }

        try {
            setPageErrors(undefined);

            setSending(true);

            if (isThreeB) {
                await finalizeMutate({
                    service: 'customInvestmentService',
                    API: 'finalizeNewPortfoliosByContainerId',
                    payload: [{
                        containerId: currentInvestmentApplicationSelector?.investmentDescription?.containerId,
                        documentLanguage: formData.documentLanguage,
                    }],
                });
                await finalizeMutate({
                    service: 'customInvestmentService',
                    API: 'renderGeneralDocuments',
                    payload: [{ containerId: currentInvestmentApplicationSelector?.investmentDescription?.containerId }],
                });
            } else {
                await finalizeMutate({
                    service: 'customInvestmentService',
                    API: 'finalizeNewPortfolio',
                    payload: [{
                        ...formData,
                        investmentApplicationId: currentInvestmentApplicationId,
                        documentLanguage: formData.documentLanguage,
                        isConsultationConducted: formData.isConsultationConducted,
                        transactionByExternalReferrer: formData.transactionByExternalReferrer,
                    }],
                });
                await finalizeMutate({
                    service: 'customInvestmentService',
                    API: 'makeIbanReservation',
                    payload: [{ investmentApplicationId: currentInvestmentApplicationId }],
                });

                if (withNewAPI) {
                    await finalizeMutate({
                        service: 'customInvestmentService',
                        API: 'generateDocumentsWithNewAPI',
                        payload: [{ contactId: clientId, investmentApplicationId: currentInvestmentApplicationId, language }],
                    });
                } else {
                    await finalizeMutate({
                        service: 'customInvestmentService',
                        API: 'generateDocuments',
                        payload: [{ investmentApplicationId: currentInvestmentApplicationId }],
                    });
                }
            }

            sessionStorage.removeItem('totalAmountOfOtherAssets');
            stepComplete();
        } catch (err: any) {
            setPageErrors({
                message: err?.message || t('general.somethingWentWrong'),
            });
        } finally {
            setSending(false);
            setIsSubmitted(false);
        }
    }, [language, clientId, memberId, formData, isThreeB]);

    const yesNoOption = useMemo(() => ([
        { label: t('clientDashboard.yes'), id: true },
        { label: t('clientDashboard.no'), id: false },
    ]), []);

    const languagesOption = useMemo(() => ([
        { label: 'DE', id: 'de' },
        { label: 'FR', id: 'fr' },
        { label: 'IT', id: 'it' },
        { label: 'EN', id: 'en' },
    ]), []);

    const passData = useCallback((value, key) => {
        setError({
            ...error,
            [key]: validateSelect(value),
        });
        setFormData({
            ...formData,
            [key]: value,
        });
    }, [formData]);

    useEffect(() => {
        setFormData({
            ...formData,
            isConsultationConducted: false,
            transactionByExternalReferrer: false,
            documentLanguage: language.toLocaleLowerCase(),
        });
    }, [language]);

    const { stepComplete } = usePortfolioProcessNavigation({
        saveOnContinue: useCallback(() => onContinue({ withNewAPI: setupData?.UPLOAD_STATIC_DOCUMENT_FEATURE_FLAG }), [onContinue]),
        enableAutoStepComplete: false,
        continueInProgress: isSending,
        pageErrors,
    });

    return {
        t,
        onContinue,
        yesNoOption,
        languagesOption,
        passData,
        isSubmitted,
        isSending,
        appsList,
        loadingAppsByContainer,
        isFollowUpInvestorProfileCase,
        data: formData,
        error,
        isThreeB,
    };
};
